import { useCallback, useRef } from 'react';
import {
  ActionIcon,
  Button,
  Card,
  Flex,
  Group,
  Indicator,
  Popover,
  ScrollArea,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IconBell, IconTrash } from '@tabler/icons';
import { useReadAllNotifications } from '../../data/hooks/notifications';
import { Notification } from '../../models/notification';
import { User } from '../../models/user';
import { formatLocale } from '../../providers/dayjs-plugins';
import { notificationsRedirectTo } from '../../utils/notifications-redirect-to';
import { rootNavigate } from '../CustomRouter';

interface NotificationIndicatorProps {
  notifications: Notification[];
  user: User;
  onRead: (id: number) => void;
  getNotifications: () => void;
  loadMore: () => Promise<void>;
  hasMore: boolean;
  loadingMore: boolean;
}

interface NotificationItemProps {
  item: Notification;
  onRedirect: (notification: Notification) => void;
}

function NotificationItem({ item, onRedirect }: NotificationItemProps) {
  return (
    <Card p={8} bg="gray.1" mb={16} withBorder>
      <UnstyledButton w="100%" h="100%" onClick={() => onRedirect(item)}>
        <Text fw="bold" size="xs">
          {item.title}
        </Text>
        <Text size="xs">{item.message}</Text>
        <Text size="xs">{formatLocale(item.createdAt, 'DD MMM YY HH:mm')}</Text>
      </UnstyledButton>
    </Card>
  );
}

export function NotificationIndicator({
  notifications,
  onRead,
  user,
  getNotifications,
  loadMore,
  loadingMore,
}: NotificationIndicatorProps) {
  const {
    fetch: readAllNotificationsFetcher,
    loading: readAllNotificationsLoader,
  } = useReadAllNotifications();

  const viewportRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (viewportRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = viewportRef.current;
      if (scrollHeight - scrollTop - clientHeight < 10) {
        loadMore();
      }
    }
  };

  const handleRedirect = useCallback(
    (notification: Notification) => {
      onRead(notification.id);
      rootNavigate(notificationsRedirectTo(notification, user.role));
    },
    [onRead, user.role],
  );

  async function handleReadAllNotifications() {
    await readAllNotificationsFetcher({
      onSuccess: () => {
        getNotifications();
      },
    });
  }

  return (
    <Popover
      width={350}
      position="bottom"
      withArrow
      shadow="md"
      onOpen={() => getNotifications()}
    >
      <Popover.Target>
        <Indicator
          disabled={!notifications.length}
          inline
          color="orange"
          label={notifications.length}
          size={16}
        >
          <ActionIcon
            color="gray"
            radius={15}
            variant="light"
            onClick={() => {}}
            size={30}
          >
            <IconBell size={20} />
          </ActionIcon>
        </Indicator>
      </Popover.Target>
      <Popover.Dropdown>
        <Text size="sm">suas ultimas notificações:</Text>
        <ScrollArea
          style={{ height: 260 }}
          viewportRef={viewportRef}
          onScrollPositionChange={handleScroll}
        >
          {!!notifications.length &&
            notifications?.map((notyItem) => (
              <NotificationItem
                key={notyItem.id}
                item={notyItem}
                onRedirect={handleRedirect}
              />
            ))}
          {loadingMore && (
            <Flex justify="center" p="xs">
              <Text size="xs" color="dimmed">
                Carregando...
              </Text>
            </Flex>
          )}
        </ScrollArea>
        <Group mt={8} position="right">
          <Button
            loading={readAllNotificationsLoader}
            onClick={handleReadAllNotifications}
            title="ler todas as notificações"
            color="ltpBlue.8"
            pr={4}
            pl={4}
            variant="outline"
          >
            <Flex>
              <IconTrash size={14} />
              <Text ml={4} size={12}>
                limpar
              </Text>
            </Flex>
          </Button>
        </Group>
      </Popover.Dropdown>
    </Popover>
  );
}
